import { Payment, Restrictions } from 'payments-lib-types';
import * as R from 'ramda';
import { lensPath, min } from 'ramda';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { useCommonStore } from '../commonStore';
import { PaymentMethod } from '../depositStore';
import { createSelectors } from '../helpers';
import { mapCurrencyToMaxAmount } from './helpers';
import { getCurrentCurrencyData } from '@/helpers/common';
import { CLIENT_ENVS } from '@/helpers/constants';
import {
  amountWithCurrencySymbol,
  currencyFlags,
  getCurrencyName,
} from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { fetchPaymentMethods } from '@/helpers/payments';
import { unmaskAmount } from '@/helpers/validators';

export interface WithdrawStorageState {
  activeThemeMode: ThemeMode | undefined;
  paymentMethods: PaymentMethod[] | undefined;
  paymentMethodsPending: boolean;
  currentPaymentMethod: PaymentMethod | null;
  currentAmount: number | string;
  currentAmountWithCurrency: number | string;
  defaultAmountRestrictions: Restrictions | undefined;
  previouslyUsedPaymentMethods: PaymentMethod[] | undefined;
  fetchPaymentMethods: () => void;
  setCurrentAmount: (amount: number | string) => void;
  setDefaultAmountRestrictions: () => void;
}

const useWithdrawStoreBase = create<WithdrawStorageState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      activeThemeMode: ThemeMode.Light,
      paymentMethods: undefined,
      paymentMethodsPending: true,
      currentPaymentMethod: null,
      currentAmount: '',
      currentAmountWithCurrency: '',
      defaultAmountRestrictions: undefined,
      previouslyUsedPaymentMethods: undefined,
      fetchPaymentMethods: async () => {
        try {
          const { user } = useCommonStore.getState();
          if (!user?.currency) return;

          set({ paymentMethodsPending: true });
          const { data } = await fetchPaymentMethods({
            currency: user.currency,
            paymentAction: Payment.Action.Cashout,
          });

          const paymentMethods = data
            .map((paymentMethod) => ({
              ...paymentMethod,
              internalId: paymentMethod.id,
            }))
            .map((paymentMethod) => {
              const restrictions = paymentMethod.termsOfService.restrictions;
              const maxAmountValue = (() => {
                const maxAmount =
                  mapCurrencyToMaxAmount[restrictions.amountCurrencyCode];
                if (maxAmount && restrictions.maxAmountValue) {
                  return min(maxAmount, restrictions.maxAmountValue);
                }
                return null;
              })();
              return R.set(
                lensPath(['termsOfService', 'restrictions', 'maxAmountValue']),
                maxAmountValue,
                paymentMethod
              );
            });
          set({ paymentMethods, paymentMethodsPending: false });
        } catch (error) {
          console.log(error);
          set({ paymentMethodsPending: false });
        }
      },
      setCurrentAmount: (currentAmount: number | string) => {
        const { user, currencies } = useCommonStore.getState();

        const currencyConversion = (amount: number, currency: string) => {
          return getCurrentCurrencyData(amount, currency, currencies).currency;
        };

        const currentAmountWithCurrency = (() => {
          if (!user?.currency) return 0;

          const amount = unmaskAmount(currentAmount);
          const currency = currencyConversion(amount, user.currency);

          return amountWithCurrencySymbol(
            getCurrencyName(currency),
            currentAmount
          );
        })();
        set({ currentAmount, currentAmountWithCurrency });
      },
      setDefaultAmountRestrictions: () => {
        const { user, countryCode } = useCommonStore.getState();
        const { currentPaymentMethod } = get();

        if (!currentPaymentMethod || !countryCode) return;

        const restrictions = currentPaymentMethod.termsOfService.restrictions;

        const defaultAmountRestrictions = {
          minAmountValue: restrictions.minAmountValue ?? null,
          maxAmountValue: restrictions.maxAmountValue ?? null,
          amountCurrencyCode: user?.currency || currencyFlags[countryCode],
        };

        set({ defaultAmountRestrictions });
      },
    })),
    { name: 'useWithdrawStore', enabled: CLIENT_ENVS.APP_ENV === 'development' }
  )
);

export const useWithdrawStore = createSelectors(useWithdrawStoreBase);
